import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL } from "../../../../config";
import { buildScopeNameList, hydrateEstimate } from "../../../../_utils/manualEstimatePayload";
import { buildCountertopOrderItems, buildScopeBlockRows } from "../../../../_utils/countertopOrderItems";
import { formatCurrency } from "../../../../_utils/manualQuantityScopes";

// Two-step modal:
//   Step 1 — nameList thumbnails (left-top) + product structure checkboxes (left-bottom) + selected photo (right)
//   Step 2 — Installation details (left) + selected photo (right)
const ConvertToOrderModal = ({
  show,
  onClose,
  token,
  contactInfoId,
  unitTypeId,
  cnCMeasureQuantityId,
  floorPlanName = "",
  structureNameMap = {},
  userId = null,
  uniqueChannelName = "",
  salesPersonId = null,
  requirePOToInstall = false,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Unit name autocomplete
  const [unitName, setUnitName] = useState("");
  const [allUnits, setAllUnits] = useState([]);
  const [unitSuggestions, setUnitSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitNameError, setUnitNameError] = useState("");

  const [measureImagePath, setMeasureImagePath] = useState(null);

  // First scope's photo — a fallback for the right-panel preview when no measure image is available.
  const [selectedNameItem, setSelectedNameItem] = useState(null);

  // Full hydrated estimate (material + labor) kept for payload building
  const [hydratedEstimate, setHydratedEstimate] = useState({ materialList: [], laborList: [] });

  // One row per scope block, carrying its room name, photo + total. Keyed by block so two scopes
  // of the same product type (e.g. two Thermofoil rooms) stay distinct.
  // Each selected scope becomes its own order.
  const [scopeRows, setScopeRows] = useState([]);
  const [selectedScopes, setSelectedScopes] = useState({});

  // Step 2
  const [installDates, setInstallDates] = useState({});
  const [occupied, setOccupied] = useState("No");
  const [purchaseOrder, setPurchaseOrder] = useState("");
  const [purchaseOrderError, setPurchaseOrderError] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [emailError, setEmailError] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [contactNumberError, setContactNumberError] = useState("");

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  const isValidPhone = (val) => /^[+]?[\d\s\-(). ]{7,}$/.test(val.trim());

  const fetchMeasureImage = async () => {
    if (!token || !contactInfoId || !unitTypeId) return;
    try {
      const res = await axios.get(
        `${API_URL}/User/api/User/GetUnitMixDataByContactInfo?PageIndex=1&PageSize=20&sort=asc&ContactInfoId=${contactInfoId}&UserId=${userId || ""}&UnitTypeId=${unitTypeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = res.data?.result ?? res.data ?? [];
      const list = Array.isArray(result) ? result : [];
      const path = list[0]?.imagePath || null;
      setMeasureImagePath(path);
    } catch (err) {
      console.error("GetUnitMixDataByContactInfo error:", err);
    }
  };

  const fetchUnits = async () => {
    if (!token || !contactInfoId || !unitTypeId) return;
    try {
      const res = await axios.get(
        `${API_URL}/Ordering/api/Order/GetCustomerUnitMixList?ContactInfoId=${contactInfoId}&unitTypeId=${unitTypeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data?.result ?? res.data ?? [];
      const list = Array.isArray(data) ? data : [];
      setAllUnits(list.filter((u) => Number(u.unitTypeId) === Number(unitTypeId)));
    } catch (err) {
      console.error("GetCustomerUnitMixList error:", err);
    }
  };

  const fetchEstimate = async () => {
    if (!token || !contactInfoId || !unitTypeId) return;
    setLoading(true);
    try {
      const cnCId = Number(cnCMeasureQuantityId) || 0;
      const url =
        `${API_URL}/Ordering/api/Order/GetCountertopCabinetEstimate` +
        `?ContactInfoId=${contactInfoId}&UnitTypeId=${unitTypeId}` +
        (cnCId > 0 ? `&CnCMeasureQuantityId=${cnCId}` : "");
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

      // Hydrate to get material/labor with structureName
      const hydrated = hydrateEstimate(res.data);
      setHydratedEstimate(hydrated);
      const allItems = [...(hydrated.materialList || []), ...(hydrated.laborList || [])];
      // Attach the scope name to each nameList entry (shown under "Select Layout as Starting Point").
      const nl = buildScopeNameList(allItems).map((entry) => {
        const match = allItems.find(
          (i) =>
            Number(i.productStructureId) === Number(entry.productStructureId) &&
            Number(i.scopeBlockId || 0) === Number(entry.scopeBlockId || 0)
        );
        return { ...entry, scopeName: (match && match.scopeName) || "" };
      });
      setSelectedNameItem(nl[0] || null);

      // One row per scope block, labeled by its main structure. Cross-structure items remain in
      // their owning block during conversion. Pre-select all blocks.
      const rows = buildScopeBlockRows(allItems, structureNameMap);
      setScopeRows(rows);
      const sel = {};
      rows.forEach((r) => { sel[r.key] = true; });
      setSelectedScopes(sel);

    } catch (err) {
      console.error("GetCountertopCabinetEstimate error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setInstallDates({});
    setOccupied("No");
    setPurchaseOrder("");
    setPurchaseOrderError("");
    setSpecialInstructions("");
    setEmailConfirmation("");
    setEmailError("");
    setContactNumber("");
    setContactNumberError("");
    setUnitName("");
    setSelectedUnit(null);
    setUnitNameError("");
    setAllUnits([]);
    setUnitSuggestions([]);
    setShowSuggestions(false);
    setMeasureImagePath(null);
    fetchEstimate();
    fetchUnits();
    fetchMeasureImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const toggleScope = (key) =>
    setSelectedScopes((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedScopeRows = scopeRows.filter((r) => selectedScopes[r.key]);
  // One installation date per selected SCOPE (labeled by product structure + room name), keyed by
  // the scope key so two scopes of the same product type each get their own date field.
  const dateTargets =
    selectedScopeRows.length > 0
      ? selectedScopeRows.map((r) => ({
          key: r.key,
          label: r.roomName ? `${r.primaryStructureName} — ${r.roomName}` : r.primaryStructureName,
        }))
      : [{ key: "__installation__", label: "Installation" }];
  const allDatesSelected = dateTargets.every((t) => !!installDates[t.key]);

  const handleGenerateOrder = async () => {
    if (!allDatesSelected) return;
    let hasError = false;
    if (requirePOToInstall && !purchaseOrder.trim()) {
      setPurchaseOrderError("Purchase Order is required.");
      hasError = true;
    }
    if (emailConfirmation.trim() && !isValidEmail(emailConfirmation)) {
      setEmailError("Please enter a valid email address.");
      hasError = true;
    }
    if (contactNumber.trim() && !isValidPhone(contactNumber)) {
      setContactNumberError("Please enter a valid phone number.");
      hasError = true;
    }
    if (hasError) return;
    const { materialList = [], laborList = [] } = hydratedEstimate;

    const orderItems = buildCountertopOrderItems({
      scopes: selectedScopeRows,
      installDates,
      materialList,
      laborList,
    });

    const formData = new FormData();
    formData.append("OrderPDF", "");
    formData.append("ClientPDF", "");
    formData.append("uniqueChannelName", uniqueChannelName || "");
    formData.append("ContactInfoId", contactInfoId);
    formData.append("unitTypeId", unitTypeId);
    formData.append("cnCMeasureQuantityId", cnCMeasureQuantityId || 0);
    formData.append("floorplan", measureImagePath || selectedNameItem?.photoUrl || "");
    formData.append("orderItems", JSON.stringify(orderItems));
    formData.append("Occupaied", occupied);
    formData.append("PurchaseOrder", purchaseOrder);
    formData.append("Instruction", specialInstructions);
    formData.append("Email", emailConfirmation);
    formData.append("PhoneNo", contactNumber);
    formData.append("CreatedBy", userId || "");
    formData.append("unitId", selectedUnit?.unitId ?? selectedUnit?.id ?? "");
    formData.append("UnitName", unitName || "");
    formData.append("UnitType", selectedUnit?.unitType ?? selectedUnit?.type ?? "");
    formData.append("SalesPersonId", salesPersonId || "");

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/Ordering/api/Order/SaveCnCOrder`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success("Order generated successfully.");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Failed to generate order. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to generate order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (!unitName.trim()) {
      setUnitNameError("Unit Number is required.");
      return;
    }
    if (!selectedUnit) {
      setUnitNameError("Please select a valid unit number from the list.");
      return;
    }
    setStep(2);
  };

  const rightImage = measureImagePath || selectedNameItem?.photoUrl || null;
  const anySelected = selectedScopeRows.length > 0;

  // Estimate identity shown under "Select Layout as Starting Point": the estimate photo
  // (scopePhotoUrl, uploaded next to "Estimate Name"), falling back to the large preview image.
  const estimateName = hydratedEstimate.scopeName || floorPlanName || "Estimate";
  const estimatePhoto = hydratedEstimate.scopePhotoUrl || rightImage;

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton style={{ backgroundColor: "#2D831F", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>
          {step === 1 ? "Convert to Measure — Select Layout" : "Convert to Measure — Installation Details"}
          {floorPlanName ? ` — ${floorPlanName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ overflow: "hidden" }}>
        {loading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "400px" }}>
            <Spinner animation="border" style={{ color: "#2D831F" }} />
          </div>
        ) : (
          <Row>
            {/* ── Left panel ─────────────────────────────────── */}
            <Col md={step === 1 ? 4 : 5} className="border-end pe-4" style={{ overflowY: "auto", maxHeight: "65vh" }}>

              {step === 1 ? (
                <>
                  {/* Unit Name autocomplete */}
                  <Form.Group className="mb-3 position-relative">
                    <Form.Label className="small fw-semibold">Unit Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={unitName}
                      placeholder="Search unit number…"
                      autoComplete="off"
                      isInvalid={!!unitNameError}
                      onChange={(e) => {
                        const val = e.target.value;
                        setUnitName(val);
                        setSelectedUnit(null);
                        setUnitNameError("");
                        if (val.trim()) {
                          const filtered = allUnits.filter((u) =>
                            (u.unitName || u.name || "").toLowerCase().includes(val.toLowerCase())
                          );
                          setUnitSuggestions(filtered);
                          setShowSuggestions(true);
                        } else {
                          setUnitSuggestions([]);
                          setShowSuggestions(false);
                        }
                      }}
                      onFocus={() => {
                        if (unitName.trim() && unitSuggestions.length > 0) setShowSuggestions(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 150);
                        if (unitName.trim() && !selectedUnit) {
                          setUnitNameError("Please select a valid unit number from the list.");
                        }
                      }}
                    />
                    {showSuggestions && unitSuggestions.length > 0 && (
                      <ul
                        className="list-unstyled mb-0 border rounded bg-white position-absolute w-100"
                        style={{ zIndex: 1050, maxHeight: "160px", overflowY: "auto", top: "100%", left: 0 }}
                      >
                        {unitSuggestions.map((u, idx) => (
                          <li
                            key={u.unitId ?? u.id ?? idx}
                            className="px-3 py-2"
                            style={{ cursor: "pointer", fontSize: "13px" }}
                            onMouseDown={() => {
                              setUnitName(u.unitName || u.name || "");
                              setSelectedUnit(u);
                              setUnitNameError("");
                              setShowSuggestions(false);
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f4f0")}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                          >
                            {u.unitName || u.name || ""}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {unitNameError}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Estimate identity — estimate photo (currently the large preview image) with the
                      estimate name captioned underneath. */}
                  <h6 className="fw-bold mb-3">Select Layout as Starting Point</h6>
                  <div className="text-center mb-4" style={{ width: "150px" }}>
                    <div
                      className="rounded overflow-hidden"
                      style={{
                        width: "150px", height: "112px",
                        border: "2px solid #dee2e6", backgroundColor: "#f8f9fa",
                      }}
                    >
                      {estimatePhoto ? (
                        <img
                          src={estimatePhoto}
                          alt={estimateName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted" style={{ fontSize: "10px" }}>
                          No img
                        </div>
                      )}
                    </div>
                    <div className="mt-1 fw-bold" style={{ wordBreak: "break-word", color: "#2D831F" }}>
                      {estimateName}
                    </div>
                  </div>

                  {/* Scopes — one row per scope (product structure + room name) */}
                  <hr />
                  <h6 className="fw-bold mb-2">Select Area to Order</h6>
                  {scopeRows.length === 0 ? (
                    <p className="text-muted small">No scopes found.</p>
                  ) : (
                    <>
                      {scopeRows.map((r) => (
                        <div key={r.key} className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`scope-chk-${r.key}`}
                            checked={!!selectedScopes[r.key]}
                            onChange={() => toggleScope(r.key)}
                            label={
                              <span style={{ color: "#2D831F", fontWeight: 600 }}>
                                {r.roomName || r.primaryStructureName}
                                {r.roomName && r.primaryStructureName ? (
                                  <span className="text-muted"> — {r.primaryStructureName}</span>
                                ) : null}
                              </span>
                            }
                          />
                          <span className="fw-semibold small ms-2">
                            Estimate Total: ${formatCurrency(r.total)}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <>
                  <h6 className="fw-bold mb-3">Installation Details</h6>

                  {dateTargets.map((t) => (
                    <Form.Group key={t.key} className="mb-3">
                      <Form.Label className="small fw-semibold">
                        Est. Installation Date — {t.label} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={installDates[t.key] || ""}
                        min={new Date().toISOString().split("T")[0]}
                        isInvalid={!installDates[t.key]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInstallDates((prev) => ({ ...prev, [t.key]: value }));
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Installation date is required.
                      </Form.Control.Feedback>
                    </Form.Group>
                  ))}

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Occupied?</Form.Label>
                    <Form.Select value={occupied} onChange={(e) => setOccupied(e.target.value)}>
                      <option>No</option>
                      <option>Yes</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">
                      Purchase Order{requirePOToInstall && <span className="text-danger"> *</span>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={purchaseOrder}
                      isInvalid={!!purchaseOrderError}
                      onChange={(e) => { setPurchaseOrder(e.target.value); setPurchaseOrderError(""); }}
                      onBlur={() => {
                        if (requirePOToInstall && !purchaseOrder.trim())
                          setPurchaseOrderError("Purchase Order is required.");
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{purchaseOrderError}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Special Instructions, like layout changes or room changes</Form.Label>
                    <Form.Control as="textarea" rows={3} value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Email Order Confirmation to Others</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="name@example.com"
                      value={emailConfirmation}
                      isInvalid={!!emailError}
                      onChange={(e) => { setEmailConfirmation(e.target.value); setEmailError(""); }}
                      onBlur={() => {
                        if (emailConfirmation.trim() && !isValidEmail(emailConfirmation))
                          setEmailError("Please enter a valid email address.");
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Contact Number in case of Questions</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Phone number"
                      value={contactNumber}
                      isInvalid={!!contactNumberError}
                      onChange={(e) => { setContactNumber(e.target.value); setContactNumberError(""); }}
                      onBlur={() => {
                        if (contactNumber.trim() && !isValidPhone(contactNumber))
                          setContactNumberError("Please enter a valid phone number.");
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{contactNumberError}</Form.Control.Feedback>
                  </Form.Group>
                </>
              )}
            </Col>

            {/* ── Right panel — selected photo ──────────────── */}
            <Col md={step === 1 ? 8 : 7} className="ps-4 d-flex align-items-center justify-content-center" style={{ overflow: "hidden" }}>
              {rightImage ? (
                <img
                  src={rightImage}
                  alt="Selected floorplan"
                  style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }}
                />
              ) : (
                <p className="text-muted text-center">No image available for the selected item.</p>
              )}
            </Col>
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 1 ? (
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              style={{ backgroundColor: "#2D831F", borderColor: "#2D831F" }}
              onClick={handleNext}
              disabled={!anySelected}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
            <Button
              style={{ backgroundColor: "#2D831F", borderColor: "#2D831F" }}
              onClick={handleGenerateOrder}
              disabled={!allDatesSelected || saving}
            >
              {saving ? (
                <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
              ) : "Generate Order"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ConvertToOrderModal;
